import { supabase } from "@/integrations/supabase/client";

export type PaymentStatus = "pending" | "paid";
export type PaymentUrgency = "overdue" | "today" | "soon" | "later" | "paid";

export interface ScheduledPayment {
  id: string;
  company_id: string;
  supplier_name: string;
  supplier_tax_id: string;
  invoice_number: string;
  invoice_date: string | null;
  amount: number;
  due_date: string;
  bank_account: string;
  authorized_by: string;
  notes: string;
  status: PaymentStatus;
  paid_at: string | null;
  file_path: string | null;
  file_name: string | null;
  source: string;
}

export type ScheduledPaymentInput = Omit<ScheduledPayment, "id" | "status" | "paid_at" | "file_path" | "file_name">;

/** Fecha local en formato YYYY-MM-DD. */
export const todayIso = (d = new Date()) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const addDays = (iso: string, days: number) => {
  const [y, m, d] = iso.split("-").map(Number);
  return todayIso(new Date(y, m - 1, d + days));
};

/** Semáforo: vencido, hoy, próximos 7 días, más adelante o pagado. */
export const paymentUrgency = (p: Pick<ScheduledPayment, "status" | "due_date">, today = todayIso()): PaymentUrgency => {
  if (p.status === "paid") return "paid";
  if (p.due_date < today) return "overdue";
  if (p.due_date === today) return "today";
  if (p.due_date <= addDays(today, 7)) return "soon";
  return "later";
};

export const loadScheduledPayments = async (companyId?: string) => {
  let q = supabase.from("scheduled_payments").select("*").order("due_date", { ascending: true });
  if (companyId) q = q.eq("company_id", companyId);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as unknown as ScheduledPayment[];
};

/** Nº de pagos pendientes vencidos o para hoy (todas las empresas accesibles). */
export const countDuePayments = async () => {
  const { count } = await supabase
    .from("scheduled_payments")
    .select("id", { count: "exact", head: true })
    .eq("status", "pending")
    .lte("due_date", todayIso());
  return count ?? 0;
};

const safeName = (n: string) => n.replace(/[^\w.\-]+/g, "_").slice(-120);

export const createScheduledPayment = async (input: ScheduledPaymentInput, file?: File | null) => {
  let file_path: string | null = null;
  let file_name: string | null = null;
  if (file) {
    file_name = file.name;
    file_path = `${input.company_id}/${crypto.randomUUID()}_${safeName(file.name)}`;
    const { error: upErr } = await supabase.storage.from("payment-attachments").upload(file_path, file, {
      contentType: file.type || "application/pdf",
    });
    if (upErr) throw upErr;
  }
  const { data: u } = await supabase.auth.getUser();
  const { error } = await supabase.from("scheduled_payments").insert({
    ...input,
    file_path,
    file_name,
    created_by: u.user?.id ?? null,
  } as any);
  if (error) throw error;
};

export const updateScheduledPayment = async (id: string, patch: Partial<ScheduledPayment>) => {
  const { error } = await supabase.from("scheduled_payments").update(patch as any).eq("id", id);
  if (error) throw error;
};

export const deleteScheduledPayment = async (p: ScheduledPayment) => {
  if (p.file_path) await supabase.storage.from("payment-attachments").remove([p.file_path]);
  const { error } = await supabase.from("scheduled_payments").delete().eq("id", p.id);
  if (error) throw error;
};

export const downloadPaymentAttachment = async (p: ScheduledPayment) => {
  if (!p.file_path) return null;
  const { data, error } = await supabase.storage.from("payment-attachments").download(p.file_path);
  if (error) throw error;
  return data;
};
